import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, Users, FolderKanban, FileText, Calendar, 
  Settings, LogOut, Menu, X, ChevronDown, Bell, Search,
  Briefcase, Receipt, PenTool, Code, UserCircle, Building2, MessageCircle, ExternalLink, Mail, Database, Archive, Package, CreditCard, Contact, RefreshCw, Shield, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/crm' },
  { icon: Contact, label: 'Clients', path: '/crm/clients', adminOnly: true },
  { icon: Users, label: 'Leads', path: '/crm/leads' },
  { icon: Briefcase, label: 'Projects', path: '/crm/projects' },
  { icon: FolderKanban, label: 'Tasks', path: '/crm/tasks' },
  { icon: Contact, label: 'Contacts', path: '/crm/contacts' },
  { icon: Calendar, label: 'Bookings', path: '/crm/bookings' },
  { icon: Receipt, label: 'Invoices', path: '/crm/invoices' },
  { icon: RefreshCw, label: 'Recurring', path: '/crm/recurring', adminOnly: true },
  { icon: PenTool, label: 'Signatures', path: '/crm/signatures' },
  { icon: CreditCard, label: 'Billing', path: '/crm/billing', adminOnly: true },
  { icon: Database, label: 'NPI Registry', path: '/crm/npi-registry', adminOnly: true },
  { icon: Archive, label: 'Already Pitched', path: '/crm/pitched-leads', adminOnly: true },
];

export const CRMLayout = ({ children }) => {
  const { user, logout, isAdmin, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/crm/login');
  };

  const isActive = (path) => {
    if (path === '/crm') return location.pathname === '/crm';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 bg-[#0A1628] transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } flex flex-col`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/crm" className="flex items-center gap-3">
            {sidebarOpen ? (
              <img 
                src="/dme-logo.png" 
                alt="DME R'US Logo" 
                className="h-[102px] w-auto"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-[#014DB7] to-[#84CC16] rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-white" />
              </div>
            )}
          </Link>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-white p-1"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            // Skip admin-only items for non-admins
            if (item.adminOnly && !isAdmin) return null;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                  isActive(item.path)
                    ? 'bg-[#014DB7] text-white'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive(item.path) ? 'text-white' : 'text-gray-400 group-hover:text-[#84CC16]'}`} />
                {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Live Chat - Opens in Popup Window */}
        <div className="px-3 pb-2">
          <button
            onClick={() => {
              const width = 500;
              const height = 700;
              const left = window.screen.width - width - 50;
              const top = 50;
              window.open(
                '/crm/live-chat-popup',
                'LiveChatWindow',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`
              );
            }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group text-gray-400 hover:bg-[#84CC16]/20 hover:text-[#84CC16] ${
              sidebarOpen ? '' : 'justify-center'
            }`}
            title="Open Live Chat in new window"
          >
            <MessageCircle className="w-5 h-5 flex-shrink-0 group-hover:text-[#84CC16]" />
            {sidebarOpen && (
              <>
                <span className="text-sm font-medium flex-1">Live Chat</span>
                <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100" />
              </>
            )}
          </button>
        </div>

        {/* Check Mail - Opens Webmail in Popup Window */}
        <div className="px-3 pb-2">
          <button
            onClick={() => {
              const width = 1200;
              const height = 800;
              const left = (window.screen.width - width) / 2;
              const top = (window.screen.height - height) / 2;
              window.open(
                'https://webmail.emailpnl.com',
                'WebmailWindow',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`
              );
            }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group text-gray-400 hover:bg-[#014DB7]/20 hover:text-[#014DB7] ${
              sidebarOpen ? '' : 'justify-center'
            }`}
            title="Open Webmail in new window"
          >
            <Mail className="w-5 h-5 flex-shrink-0 group-hover:text-[#014DB7]" />
            {sidebarOpen && (
              <>
                <span className="text-sm font-medium flex-1">Check Mail</span>
                <ExternalLink className="w-4 h-4 opacity-50 group-hover:opacity-100" />
              </>
            )}
          </button>
        </div>

        {/* User Section */}
        <div className="p-3 border-t border-white/10">
          <Link
            to="/crm/profile"
            className={`flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors ${sidebarOpen ? '' : 'justify-center'}`}
          >
            <div className="w-9 h-9 bg-gradient-to-br from-[#014DB7] to-[#84CC16] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-semibold text-sm">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </span>
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-400 truncate capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            )}
          </Link>
          <button
            onClick={handleLogout}
            className={`mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors ${
              sidebarOpen ? '' : 'justify-center'
            }`}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 bg-gray-100 rounded-xl text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            <div className="relative">
              <button 
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-[#014DB7] to-[#84CC16] rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="font-medium text-gray-900">{user?.first_name} {user?.last_name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                  <Link
                    to="/crm/profile"
                    className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <UserCircle className="w-4 h-4" />
                    <span>Profile</span>
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/crm/admin"
                      className="flex items-center gap-2 px-4 py-2 text-[#84CC16] hover:bg-[#84CC16]/5 font-medium"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Shield className="w-4 h-4" />
                      <span>Admin Dashboard</span>
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/crm/settings"
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Admin Settings</span>
                    </Link>
                  )}
                  {isSuperAdmin && (
                    <Link
                      to="/crm/dev-settings"
                      className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Code className="w-4 h-4" />
                      <span>Dev Settings</span>
                    </Link>
                  )}
                  {isSuperAdmin && (
                    <Link
                      to="/crm/feature-installer"
                      className="flex items-center gap-2 px-4 py-2 text-purple-600 hover:bg-purple-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Package className="w-4 h-4" />
                      <span>Feature Installer</span>
                    </Link>
                  )}
                  {isSuperAdmin && (
                    <Link
                      to="/crm/super-admin"
                      className="flex items-center gap-2 px-4 py-2 text-[#014DB7] hover:bg-[#014DB7]/5 font-medium"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Super Admin Panel</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default CRMLayout;
